/**
 * Hook for client-side help article search
 * Provides debounced search with instant results
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { HelpArticleSummary, HelpCategory } from '../types';

interface UseHelpSearchOptions {
  articles: HelpArticleSummary[];
  categories?: HelpCategory[];
  debounceMs?: number;
  maxResults?: number;
}

interface UseHelpSearchResult {
  query: string;
  setQuery: (query: string) => void;
  results: HelpArticleSummary[];
  isSearching: boolean;
  hasResults: boolean;
  clearSearch: () => void;
  getCategoryName: (categoryId: string) => string;
}

/**
 * Hook for searching help articles
 */
export function useHelpSearch({
  articles,
  categories = [],
  debounceMs = 300,
  maxResults = 10,
}: UseHelpSearchOptions): UseHelpSearchResult {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Debounce the search query
  useEffect(() => {
    if (!query.trim()) {
      setDebouncedQuery('');
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
      setIsSearching(false);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [query, debounceMs]);

  // Category lookup map
  const categoryMap = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach(cat => {
      map.set(cat.id, cat.name);
    });
    return map;
  }, [categories]);

  // Search results
  const results = useMemo(() => {
    if (!debouncedQuery.trim()) {
      return [];
    }

    const searchTerms = debouncedQuery.toLowerCase().split(/\s+/).filter(Boolean);

    // Score articles based on match quality
    const scored = articles.map(article => {
      let score = 0;
      const titleLower = article.title.toLowerCase();
      const excerptLower = article.excerpt.toLowerCase();

      searchTerms.forEach(term => {
        // Title matches are worth more
        if (titleLower.includes(term)) {
          score += 10;
          // Bonus for title starting with term
          if (titleLower.startsWith(term)) {
            score += 5;
          }
        }

        // Excerpt matches
        if (excerptLower.includes(term)) {
          score += 3;
        }
      });

      // Boost featured articles slightly
      if (article.featured) {
        score += 1;
      }

      return { article, score };
    });

    // Filter and sort by score
    return scored
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, maxResults)
      .map(item => item.article);
  }, [articles, debouncedQuery, maxResults]);

  // Clear search
  const clearSearch = useCallback(() => {
    setQuery('');
    setDebouncedQuery('');
    setIsSearching(false);
  }, []);

  // Get category name helper
  const getCategoryName = useCallback((categoryId: string) => {
    return categoryMap.get(categoryId) || categoryId;
  }, [categoryMap]);

  return {
    query,
    setQuery,
    results,
    isSearching,
    hasResults: results.length > 0,
    clearSearch,
    getCategoryName,
  };
}

export default useHelpSearch;
